import { usePageTitle } from "@/hooks/usePageTitle";
import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Play, FileText, Download, LogOut, Apple, ChefHat, GraduationCap, Loader2, X, Zap, KeyRound, FolderOpen, Search, CreditCard, PauseCircle, Facebook, ExternalLink, Lock, ArrowUpCircle } from "lucide-react";
import { FeedbackWidget } from "@/components/FeedbackWidget";
import { BiaTracker } from "@/components/BiaTracker";
import { AthletesHub } from "@/components/AthletesHub";
import { WelcomeGuide } from "@/components/WelcomeGuide";
import { toast } from "sonner";

const LOGO = "https://customer-assets-jai6qajn.emergentagent.net/job_inbody-powered/artifacts/cgjm5igj_FB%20Cover%20logo.png";
const CAT_ICONS = { nutrition: Apple, recipes: ChefHat, teaching: GraduationCap, athletes: Zap };

function SearchBox({ value, onChange, placeholder, testId }) {
  return (
    <div className="relative w-full sm:w-72">
      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8A29E]" />
      <input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full bg-white border border-[#E7E5E4] rounded-full pl-10 pr-9 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal" />
      {value && (
        <button onClick={() => onChange("")} data-testid={`${testId}-clear`}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A8A29E] hover:text-navy">
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function RecipeBanner({ recipe }) {
  return (
    <div className="bg-white border border-[#E7E5E4] rounded-2xl shadow-sm overflow-hidden flex flex-col sm:flex-row" data-testid="recipe-of-week">
      {recipe.image_url && (
        <div className="sm:w-64 h-44 sm:h-auto shrink-0">
          <img src={recipe.image_url} alt={recipe.title} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="p-6 flex flex-col justify-center">
        <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] font-semibold text-teal">
          <ChefHat className="w-4 h-4" /> Recipe of the week
        </span>
        <h3 className="mt-2 font-heading text-2xl text-navy">{recipe.title}</h3>
        {recipe.description && <p className="mt-1.5 text-sm text-[#57534E] leading-relaxed">{recipe.description}</p>}
        {recipe.pdf_url && (
          <a href={fileUrl(recipe.pdf_url)} target="_blank" rel="noreferrer" data-testid="recipe-pdf-btn"
            className="mt-4 inline-flex items-center gap-2 self-start bg-teal text-white hover:bg-teal-dark transition-colors rounded-full px-5 py-2 text-sm font-medium">
            <Download className="w-4 h-4" /> View / download recipe (PDF)
          </a>
        )}
      </div>
    </div>
  );
}

function PausedBanner({ user, onResumed }) {
  const [busy, setBusy] = useState(false);
  const until = user?.paused_until ? new Date(user.paused_until).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "";
  return (
    <div className="mt-8 bg-white border border-[#E7E5E4] rounded-2xl p-10 text-center shadow-sm" data-testid="paused-banner">
      <div className="mx-auto w-14 h-14 rounded-full bg-teal/10 text-teal flex items-center justify-center"><PauseCircle className="w-7 h-7" /></div>
      <h2 className="mt-4 font-heading text-2xl text-navy">Your membership is paused</h2>
      <p className="mt-2 text-[#57534E] max-w-md mx-auto">Billing is paused and your access will resume automatically on <strong>{until}</strong>. Want back in sooner?</p>
      <button disabled={busy} onClick={async () => {
        setBusy(true);
        try {
          await api.post("/payments/resume");
          toast.success("Welcome back! Your membership is active again.");
          if (onResumed) await onResumed();
        } catch (e) {
          toast.error(errMsg(e, "Could not resume. Please try again shortly."));
        } finally { setBusy(false); }
      }} data-testid="resume-membership-btn"
        className="mt-6 inline-flex items-center gap-2 bg-teal text-white hover:bg-teal-dark rounded-full px-7 py-3 text-sm font-semibold disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Resume my membership now
      </button>
      <p className="mt-3 text-xs text-[#78716C]">You can pause up to three times a year, one month at a time.</p>
    </div>
  );
}

function PauseDialog({ user, onClose, onPaused }) {
  const [busy, setBusy] = useState(false);
  const remaining = Math.max(0, 3 - (user?.pauses_used_this_year || 0));
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-7" onClick={(e) => e.stopPropagation()} data-testid="pause-dialog">
        <h3 className="font-heading text-xl text-navy">Pause your membership</h3>
        <ul className="mt-4 space-y-2 text-sm text-[#57534E]">
          <li>• Your membership pauses for <strong>one month</strong> from today — billing and portal access stop straight away.</li>
          <li>• Everything resumes automatically after the month, or you can resume early at any time.</li>
          <li>• You can pause up to <strong>3 times a year</strong> — each pause counts, even if you resume early. You have <strong>{remaining} pause{remaining === 1 ? "" : "s"}</strong> left this year.</li>
        </ul>
        {remaining === 0 && <p className="mt-3 text-xs text-red-600" data-testid="pause-limit-msg">You've used all 3 pauses for this year.</p>}
        <div className="mt-6 flex gap-3 justify-end">
          <button onClick={onClose} data-testid="pause-cancel-btn" className="px-5 py-2.5 rounded-full text-sm font-medium border border-[#E7E5E4] text-[#57534E] hover:bg-[#F5F5F4]">Not now</button>
          <button disabled={busy || remaining === 0} onClick={async () => {
            setBusy(true);
            try {
              const { data } = await api.post("/payments/pause");
              toast.success(`Membership paused until ${new Date(data.paused_until).toLocaleDateString("en-GB", { day: "numeric", month: "long" })}`);
              if (onPaused) await onPaused();
              onClose();
            } catch (e) {
              toast.error(errMsg(e, "Could not pause. Please try again shortly."));
            } finally { setBusy(false); }
          }} data-testid="pause-confirm-btn"
            className="px-5 py-2.5 rounded-full text-sm font-semibold bg-navy text-white hover:bg-navy-dark disabled:opacity-60 inline-flex items-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Pause for 1 month
          </button>
        </div>
      </div>
    </div>
  );
}

const IC_GOLD = "#C9A227";
const IC_LOGO = "https://customer-assets-jai6qajn.emergentagent.net/job_inbody-powered/artifacts/371pmp6w_Inner%20Circle%20Logo.png";

function InnerCircleUpgradeCard({ user }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/inner-circle/status").then((r) => setStatus(r.data)).catch(() => {}); }, []);
  if (!status || status.sold_out) return null;
  const price = Math.round(status.price);
  const planLabel = user?.membership_plan === "virtual" ? "£18/month Virtual membership" : "monthly membership";
  const upgrade = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        plan: "inner_circle", origin_url: window.location.origin, terms_accepted: true,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(errMsg(e, "Could not start checkout. Please try again shortly."));
      setBusy(false);
    }
  };
  return (
    <div className="mt-6 bg-navy rounded-2xl p-7 sm:p-8 text-white relative overflow-hidden" style={{ border: `1px solid ${IC_GOLD}55` }} data-testid="inner-circle-upgrade-card">
      <div className="brand-blob" style={{ width: 240, height: 240, top: -70, right: -50, background: IC_GOLD, opacity: 0.22 }} />
      <div className="relative grid md:grid-cols-[1fr_auto] gap-6 items-center">
        <div>
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: IC_GOLD }}>
            <Lock className="w-4 h-4" /> MBBC Inner Circle · Only 6 places
          </span>
          <h2 className="mt-2 font-heading text-2xl sm:text-3xl">Upgrade to the Inner Circle — 8 weeks of private 1-1 coaching</h2>
          <p className="mt-3 text-sm text-white/80 leading-relaxed max-w-2xl">
            Eight weekly 30-minute 1-1 sessions with Andrea, tailored guidance across the 4 Pillars, ongoing menu plan support
            and full access to everything you already enjoy as a member. Maximum 6 people — genuinely private support, not a group programme.
          </p>
          <p className="mt-3 text-sm leading-relaxed max-w-2xl rounded-xl bg-white/10 px-4 py-3" data-testid="ic-upgrade-pause-note">
            <strong style={{ color: IC_GOLD }}>Good to know:</strong> your {planLabel} payments pause automatically when you join the
            Inner Circle and restart when your course access ends in early December — you keep full member access the whole time.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-4">
            <button disabled={busy} onClick={upgrade} data-testid="ic-upgrade-btn"
              className="inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-semibold text-navy disabled:opacity-60 hover:opacity-90 transition-opacity"
              style={{ background: IC_GOLD }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpCircle className="w-4 h-4" />} Upgrade to Inner Circle — £{price} one-off
            </button>
            <Link to="/inner-circle" data-testid="ic-upgrade-learn-more" className="text-sm font-medium text-white/80 hover:text-white underline underline-offset-4">
              Learn more about the course
            </Link>
          </div>
          <p className="mt-3 text-xs text-white/55">
            One-off payment of £{price}. By upgrading you agree to our{" "}
            <Link to="/terms" className="underline hover:text-white/80">Terms &amp; Conditions</Link>.
          </p>
        </div>
        <img src={IC_LOGO} alt="MBBC Inner Circle" className="hidden md:block w-40 h-auto rounded-2xl bg-white p-2.5" />
      </div>
    </div>
  );
}

function mediaUrl(item) {
  if (item.external_url) return item.external_url;
  const token = localStorage.getItem("mbbc_token");
  return `${api.defaults.baseURL}/content/${item.id}/file?auth=${encodeURIComponent(token || "")}`;
}

function fileUrl(url) {
  const token = localStorage.getItem("mbbc_token");
  if (!url || !token || !url.includes("/api/files/")) return url;
  return `${url}${url.includes("?") ? "&" : "?"}auth=${encodeURIComponent(token)}`;
}

function errMsg(e, fallback) {
  const d = e.response?.data?.detail;
  return typeof d === "string" && d.length < 160 && !d.includes("<") && !/cloudflare|web server/i.test(d) ? d : fallback;
}

function ContentCard({ item, label, onWatch }) {
  return (
    <div className="bg-white border border-[#E7E5E4] rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col">
      <div className={`h-40 flex items-center justify-center relative overflow-hidden ${item.type === "video" ? "bg-navy" : "bg-teal/10"}`}>
        {item.thumbnail_url && <img src={item.thumbnail_url} alt={item.title} className="absolute inset-0 w-full h-full object-cover" />}
        {item.type === "video"
          ? <div className={`relative w-14 h-14 rounded-full flex items-center justify-center ${item.thumbnail_url ? "bg-black/45" : "bg-white/15"}`}><Play className="w-6 h-6 text-white ml-0.5" /></div>
          : (!item.thumbnail_url && <FileText className="w-12 h-12 text-teal" />)}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <span className="text-xs uppercase tracking-wide text-teal font-semibold">{label || item.category}</span>
        <h3 className="mt-1.5 font-heading text-lg text-navy leading-snug">{item.title}</h3>
        <p className="mt-1.5 text-sm text-[#57534E] flex-1">{item.description}</p>
        {item.type === "video" ? (
          <button onClick={() => onWatch(item)} data-testid={`watch-${item.id}`} className="mt-4 inline-flex items-center justify-center gap-2 bg-navy text-white rounded-full py-2.5 text-sm font-medium hover:bg-navy-dark">
            <Play className="w-4 h-4" /> Watch
          </button>
        ) : (
          <a href={mediaUrl(item)} target="_blank" rel="noreferrer" data-testid={`open-${item.id}`} className="mt-4 inline-flex items-center justify-center gap-2 border border-[#E7E5E4] text-navy rounded-full py-2.5 text-sm font-medium hover:bg-[#F5F5F4]">
            <Download className="w-4 h-4" /> Open PDF
          </a>
        )}
        {item.attachment_url && (
          <a href={fileUrl(item.attachment_url)} target="_blank" rel="noreferrer" data-testid={`attachment-${item.id}`}
            className="mt-2.5 inline-flex items-center justify-center gap-2 border border-teal/40 text-teal rounded-full py-2 text-sm font-medium hover:bg-teal hover:text-white transition-colors">
            <FileText className="w-4 h-4" /> {item.attachment_name ? `Download: ${item.attachment_name}` : "Printable PDF"}
          </a>
        )}
      </div>
    </div>
  );
}

export default function ClientPortal() {
  usePageTitle("Member Portal");
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [cat, setCat] = useState("all");
  const [active, setActive] = useState(null);
  const [view, setView] = useState("welcome");
  const [recipe, setRecipe] = useState(null);
  const [cats, setCats] = useState([]);
  const [archive, setArchive] = useState(null);
  const [query, setQuery] = useState("");
  const [recipeQuery, setRecipeQuery] = useState("");
  const [showPause, setShowPause] = useState(false);
  const paused = user?.membership_status === "paused";

  useEffect(() => { api.get("/content").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  useEffect(() => { api.get("/recipe-of-week").then((r) => setRecipe(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (view === "recipes" && archive === null) {
      api.get("/recipes/archive").then((r) => setArchive(r.data)).catch(() => setArchive([]));
    }
  }, [view, archive]);

  const catList = useMemo(() => ([
    { id: "all", label: "All content", icon: Apple },
    ...cats.map((c) => ({ ...c, icon: CAT_ICONS[c.id] || FolderOpen })),
  ]), [cats]);

  const filtered = useMemo(() => {
    if (!items) return [];
    let list = cat === "all" ? items : items.filter((i) => i.category === cat);
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((i) => `${i.title} ${i.description || ""}`.toLowerCase().includes(q));
    return list;
  }, [items, cat, query]);

  const filteredArchive = useMemo(() => {
    if (!archive) return [];
    const q = recipeQuery.trim().toLowerCase();
    if (!q) return archive;
    return archive.filter((r) => `${r.title} ${r.description || ""}`.toLowerCase().includes(q));
  }, [archive, recipeQuery]);

  const athleteItems = useMemo(() => (items || []).filter((i) => i.category === "athletes"), [items]);

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <FeedbackWidget />
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-[#E7E5E4]">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO} alt="My Best Body Club" className="h-[4.5rem] w-auto object-contain rounded-lg" />
            <span className="font-heading font-semibold text-navy">My Best Body Club — Member Portal</span>
          </Link>
          <div className="flex items-center gap-4">
            {user?.role === "nutrition_therapist" && <Link to="/dashboard" className="text-sm text-teal font-medium">Therapist dashboard</Link>}
            {user?.role === "admin" && <Link to="/admin" className="text-sm text-teal font-medium">Admin CMS</Link>}
            <span className="hidden sm:block text-sm text-[#57534E]">{user?.name}</span>
            <button onClick={async () => {
              try {
                await api.post("/auth/forgot-password", { email: user.email, origin_url: window.location.origin });
                toast.success(`Password reset link sent to ${user.email}`);
              } catch { toast.error("Could not send reset link. Please try again."); }
            }} data-testid="portal-reset-password" className="inline-flex items-center gap-1.5 text-sm text-[#57534E] hover:text-navy">
              <KeyRound className="w-4 h-4" /> Reset password
            </button>
            <button onClick={async () => {
              try {
                const { data } = await api.post("/payments/billing-portal", { origin_url: window.location.origin });
                window.location.href = data.url;
              } catch (e) {
                toast.error(errMsg(e, "Could not open membership management. Please try again shortly."));
              }
            }} data-testid="portal-manage-membership" className="inline-flex items-center gap-1.5 text-sm text-[#57534E] hover:text-navy">
              <CreditCard className="w-4 h-4" /> Manage membership
            </button>
            {user?.can_pause && (
              <button onClick={() => setShowPause(true)} data-testid="portal-pause-membership" className="inline-flex items-center gap-1.5 text-sm text-[#57534E] hover:text-navy">
                <PauseCircle className="w-4 h-4" /> Pause
              </button>
            )}
            <button onClick={() => { logout(); navigate("/"); }} data-testid="portal-logout" className="inline-flex items-center gap-1.5 text-sm text-[#57534E] hover:text-navy">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 py-10">
        <div className="bg-navy rounded-2xl p-8 text-white relative overflow-hidden">
          <div className="brand-blob bg-teal" style={{ width: 280, height: 280, top: -60, right: -40 }} />
          <div className="relative">
            <h1 className="font-heading text-3xl">Welcome back, {user?.name?.split(" ")[0]} 👋</h1>
            <p className="mt-2 text-white/70 max-w-lg">Your bespoke, data-driven nutrition library — teaching videos, recipes and printable guides.</p>
            {user?.membership_plan && <span className="mt-4 inline-block bg-teal/20 text-teal-100 text-xs font-medium px-3 py-1 rounded-full capitalize">{user.membership_plan} membership · {paused ? "paused" : "active"}</span>}
          </div>
        </div>

        {paused ? (
          <PausedBanner user={user} onResumed={checkAuth} />
        ) : (<>
        {user?.membership_status === "active" && ["virtual", "physical", "hybrid"].includes(user?.membership_plan) && (
          <InnerCircleUpgradeCard user={user} />
        )}
        <div className="mt-6 flex flex-wrap gap-2.5" data-testid="portal-nav">
          {[["welcome", "Welcome"], ["library", "Content Library"], ["recipes", "Recipe Archive"], ["athletes", "Athlete Hub"], ["tracker", "My Tracker"]].map(([id, label]) => (
            <button key={id} onClick={() => setView(id)} data-testid={`view-${id}`}
              className={`px-6 py-3 text-sm font-semibold rounded-full transition-all ${view === id
                ? "bg-navy text-white shadow-lg shadow-navy/20 scale-[1.03]"
                : "bg-white text-[#57534E] border border-[#E7E5E4] hover:border-teal hover:text-navy"}`}>
              {label}
            </button>
          ))}
        </div>

        {view === "tracker" && (
          <div className="mt-8">
            <BiaTracker readingsPath="/tracker/readings" createPath="/tracker/readings" deletePathBase="/tracker/readings" memberName={user?.name} />
          </div>
        )}

        {view === "welcome" && (
          <div className="mt-8">
            {recipe && <div className="mb-8"><RecipeBanner recipe={recipe} /></div>}
            <WelcomeGuide
              handbookUrl={(() => {
                const hb = (items || []).find((i) => (i.title || "").toLowerCase().includes("handbook"));
                return hb ? mediaUrl(hb) : null;
              })()} />
            <a href="https://www.facebook.com/groups/1307130974652107" target="_blank" rel="noopener noreferrer"
              data-testid="facebook-group-card"
              className="mt-8 flex flex-wrap items-center justify-between gap-4 bg-navy text-white rounded-2xl p-6 sm:p-7 relative overflow-hidden hover:opacity-95 transition-opacity">
              <div className="brand-blob bg-teal" style={{ width: 200, height: 200, top: -60, right: -40 }} />
              <div className="relative flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#1877F2] flex items-center justify-center shrink-0">
                  <Facebook className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-heading text-xl">Join the private member Facebook group</h3>
                  <p className="mt-1 text-sm text-white/70">Questions, support and genuine community — plus direct access to Andrea.</p>
                </div>
              </div>
              <span className="relative inline-flex items-center gap-2 bg-teal text-white rounded-full px-6 py-2.5 text-sm font-semibold">
                Join the group <ExternalLink className="w-4 h-4" />
              </span>
            </a>
          </div>
        )}

        {view === "athletes" && (
          <div className="mt-8">
            <AthletesHub>
              {athleteItems.length > 0 && (
                <div>
                  <h3 className="font-heading text-2xl text-navy">Athlete Resources</h3>
                  <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="athletes-content-grid">
                    {athleteItems.map((item) => (
                      <ContentCard key={item.id} item={item} onWatch={setActive} />
                    ))}
                  </div>
                </div>
              )}
            </AthletesHub>
          </div>
        )}

        {view === "recipes" && (
          <div className="mt-8" data-testid="recipe-archive">
            <div className="flex flex-wrap items-center gap-3 justify-between">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-teal/10 text-teal flex items-center justify-center"><ChefHat className="w-5 h-5" /></div>
                <div>
                  <h2 className="font-heading text-2xl text-navy">Recipe Archive</h2>
                  <p className="text-sm text-[#78716C]">Every Recipe of the Week we've featured — revisit your favourites anytime.</p>
                </div>
              </div>
              <SearchBox value={recipeQuery} onChange={setRecipeQuery} placeholder="Search recipes…" testId="archive-search-input" />
            </div>
            {archive === null ? (
              <div className="mt-10 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-teal" /></div>
            ) : filteredArchive.length === 0 ? (
              <p className="mt-10 text-center text-[#78716C]" data-testid="archive-empty">
                {recipeQuery.trim() ? `No recipes match "${recipeQuery.trim()}" — try a different keyword.` : "No recipes archived yet — check back after the next Recipe of the Week!"}
              </p>
            ) : (
              <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="recipe-archive-grid">
                {filteredArchive.map((r) => (
                  <div key={r.id} className="bg-white border border-[#E7E5E4] rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col" data-testid={`archive-recipe-${r.id}`}>
                    <div className="h-40 bg-teal/10 flex items-center justify-center overflow-hidden">
                      {r.image_url
                        ? <img src={r.image_url} alt={r.title} className="w-full h-full object-cover" />
                        : <ChefHat className="w-12 h-12 text-teal" />}
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <span className="text-xs uppercase tracking-wide text-teal font-semibold">
                        {new Date(r.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                      </span>
                      <h3 className="mt-1.5 font-heading text-lg text-navy leading-snug">{r.title}</h3>
                      {r.description && <p className="mt-1.5 text-sm text-[#57534E] flex-1">{r.description}</p>}
                      {r.pdf_url && (
                        <a href={fileUrl(r.pdf_url)} target="_blank" rel="noreferrer" data-testid={`archive-pdf-${r.id}`}
                          className="mt-3 inline-flex items-center gap-1.5 self-start text-sm font-medium text-teal hover:text-teal-dark">
                          <Download className="w-4 h-4" /> View / download recipe
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {view === "library" && (<>
        <div className="mt-8 flex flex-wrap items-center gap-3 justify-between">
          <div className="flex flex-wrap gap-2">
            {catList.map((c) => (
              <button key={c.id} onClick={() => setCat(c.id)} data-testid={`filter-${c.id}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${cat === c.id ? "bg-navy text-white" : "bg-white border border-[#E7E5E4] text-[#57534E] hover:border-[#D6D3D1]"}`}>
                <c.icon className="w-4 h-4" /> {c.label}
              </button>
            ))}
          </div>
          <SearchBox value={query} onChange={setQuery} placeholder="Search videos, recipes, guides…" testId="library-search-input" />
        </div>

        {cat === "recipes" && recipe && <div className="mt-6"><RecipeBanner recipe={recipe} /></div>}

        {items === null ? (
          <div className="mt-12 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-teal" /></div>
        ) : filtered.length === 0 ? (
          <p className="mt-12 text-center text-[#78716C]" data-testid="library-empty">
            {query.trim() ? `No results for "${query.trim()}" — try a different keyword.` : "No content here yet. Check back soon!"}
          </p>
        ) : (
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="content-grid">
            {filtered.map((item) => (
              <ContentCard key={item.id} item={item} label={(catList.find((c) => c.id === item.category) || {}).label} onWatch={setActive} />
            ))}
          </div>
        )}
        </>)}
        </>)}
      </section>

      {showPause && <PauseDialog user={user} onClose={() => setShowPause(false)} onPaused={checkAuth} />}

      {active && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setActive(null)}>
          <div className="bg-white rounded-2xl overflow-hidden max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#E7E5E4]">
              <h3 className="font-heading text-navy">{active.title}</h3>
              <button onClick={() => setActive(null)} className="text-[#78716C] hover:text-navy"><X className="w-5 h-5" /></button>
            </div>
            <video src={mediaUrl(active)} controls autoPlay className="w-full bg-black max-h-[70vh]" data-testid="video-player" />
          </div>
        </div>
      )}
    </div>
  );
}
